import { useState } from "react";
import RichTextEditor from "../../../common/richTextEditor";
import ConfirmationPopup from "../../../common/confirmationPopup";
import { useTranslation } from "react-i18next";
import { canAction } from "../../../common/permissionhelper";
const MODULE = "manage_foodservicepartners";

const HowItWorksSectionEditor = ({ section, onUpdate, onSave, isSaving }) => {
  const { t } = useTranslation();
  const canEdit = canAction(MODULE, "edit");
  const isReadOnly = !canEdit;
  const [stepToDelete, setStepToDelete] = useState(null);
  const DEFAULT_STEPS = [
    { stepNumber: 1, title: "", description: "" },
    { stepNumber: 2, title: "", description: "" },
    { stepNumber: 3, title: "", description: "" },
    { stepNumber: 4, title: "", description: "" },
  ];
  const [formData, setFormData] = useState(() => {
    const steps =
      section?.steps && section.steps.length === 4
        ? section.steps
        : DEFAULT_STEPS;
    return section
      ? { ...section, steps }
      : { title: "", description: "", steps: DEFAULT_STEPS };
  });
  // Steps Accordion
  const [openStepIndex, setOpenStepIndex] = useState(null);
  const toggleStepAccordion = (index) => {
    setOpenStepIndex((prev) => (prev === index ? null : index));
  };
  // Whenever section prop changes update formData
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Handle rich text changes for description
  const handleRichTextChange = (html) => {
    const updatedData = {
      ...formData,
      description: html,
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Handle changes to individual steps (number, title, description)
  const handleStepChange = (index, field, value) => {
    const updatedSteps = [...formData.steps];
    updatedSteps[index] = {
      ...updatedSteps[index],
      stepNumber: index + 1,
      [field]: value,
    };
    const updatedData = {
      ...formData,
      steps: updatedSteps,
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Rendering logic
  return (
    <div className="section-editor">
      {/* Section Title */}
      <div className="input-field">
        <label>{t("section_title_txt")}</label>
        <input
          type="text"
          name="title"
          value={formData.title || ""}
          onChange={handleChange}
          placeholder={t("enter_section_title_txt")}
          readOnly={isReadOnly}
        />
      </div>
      {/* Section Description */}
      <div className="input-field">
        <label>{t("section_description_txt")}</label>
        <RichTextEditor
          emailContent={formData.description || ""}
          handleChangeEmailContent={handleRichTextChange}
          readOnly={isReadOnly}
        />
      </div>
      {/* Steps */}
      <div className="input-field">
        <div className="flex align-center justify-space ">
          <label>{t("steps_txt")}</label>
        </div>
        <div className="flex-card-row">
          {formData.steps?.map((step, index) => (
            <div className="card" key={index}>
              {/* Accordion Header */}
              <div
                className="accordion-header"
                onClick={() => toggleStepAccordion(index)}
              >
                <h4>{t("steps_txt")}{String(index + 1).padStart(2, "0")}</h4>
                <span
                  className={`arrow ${openStepIndex === index ? "open" : ""}`}
                ></span>
              </div>
              {/* Accordion Body */}
              <div
                className={`accordion-body ${
                  openStepIndex === index ? "open" : ""
                }`}
              >
                <div
                  className="steps-field"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    value={step.title || ""}
                    onChange={(e) =>
                      handleStepChange(index, "title", e.target.value)
                    }
                    placeholder={t("step_title_txt")}
                    readOnly={isReadOnly}
                  />
                  <textarea
                    value={step.description || ""}
                    onChange={(e) =>
                      handleStepChange(index, "description", e.target.value)
                    }
                    placeholder={t("step_description_txt")}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {!isReadOnly && (
        <div className="btnfield">
          <button
            title={t("save_section_txt")}
            type="button"
            onClick={() => onSave && onSave("howItWorks", formData)}
            disabled={isSaving}
          >
            {isSaving ? t("saving_txt") : t("save_section_txt")}
          </button>
        </div>
      )}
      {stepToDelete !== null && (
        <ConfirmationPopup
          title={t("confirm_txt")}
          confirmationMessage={t("confirm_delete_step_txt")}
          handleYes={() => {
            removeStep(stepToDelete);
            setStepToDelete(null);
          }}
          handleNo={() => setStepToDelete(null)}
        />
      )}
    </div>
  );
};

export default HowItWorksSectionEditor;
